/**
 * Blocked property and the reports OFAC is owed (31 CFR 501.603, 501.604; S-33). Visible only to the
 * designated compliance officer and super_admins; the server answers 403 to everyone else. Every write
 * takes the actor's step-up code.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type ReportKind = 'blocked_initial' | 'annual' | 'unblocked' | 'rejected';
export type EvidenceKind = 'ofac_license' | 'sdn_delisting';

export interface HeldAmount {
  currency: string;
  amount: string;
}

export interface BlockedProperty {
  id: string;
  subject_kind: 'position' | 'transfer';
  legal_entity_id: string;
  client_id: string | null;
  client_name: string | null;
  bank_transaction_id: string | null;
  screening_result_id: number;
  basis: string;
  blocked_at: string;
  in_force: boolean;
  released_at: string | null;
  held: HeldAmount[];
  pending_request_id: string | null;
  /** The sender the bank named on a blocked transfer. */
  sender_name: string | null;
  /** The US entity of the group that holds the property, by what it holds. */
  entity_name: string | null;
  /** What was recorded for the release that waits for its second person. */
  pending_evidence_kind: EvidenceKind | null;
  pending_evidence_reference: string | null;
  pending_evidence_reason: string | null;
  pending_evidence_has_file: boolean;
  pending_evidence_recorded_at: string | null;
  /** The latest list no longer names the person this block was confirmed on. */
  listing_ended: boolean;
}

/** A confirmed true match still in force that blocked no property: its delisting is recorded by two people. */
export interface ConfirmedMatch {
  screening_result_id: number;
  client_id: string | null;
  client_name: string | null;
  confirmed_at: string;
  /** The latest list no longer names the person. */
  listing_ended: boolean;
  pending_request_id: string | null;
  pending_evidence_reference: string | null;
  pending_evidence_reason: string | null;
  pending_evidence_has_file: boolean;
  pending_evidence_recorded_at: string | null;
}

export interface DelistingInput {
  evidence_reference: string;
  reason: string;
  attachment: File | null;
  mfa_code: string;
}

export interface ReportTask {
  id: string;
  kind: ReportKind;
  legal_entity_id: string;
  block_id: string | null;
  as_of: string | null;
  opened_at: string;
  due_on: string;
  days_left: number;
  outcome: 'filed' | 'not_required' | null;
  filed_on: string | null;
  ofac_reference: string | null;
  has_file: boolean;
  reason: string | null;
  filed_by: string | null;
  filed_at: string | null;
  needs_placement: boolean;
  placement_outcome: 'placed' | 'no_cash' | null;
  placed_on: string | null;
  account_reference: string | null;
  bank_reference: string | null;
  placement_reason: string | null;
  placement_has_file: boolean;
}

export interface PlacementInput {
  placed_on: string | null;
  account_reference: string | null;
  bank_reference: string | null;
  amount: string | null;
  currency_code: string | null;
  attachment: File | null;
  no_cash_reason: string | null;
  mfa_code: string;
}

export interface FilingInput {
  filed_on: string;
  ofac_reference: string;
  attachment: File;
  mfa_code: string;
}

export interface EvidenceInput {
  evidence_kind: EvidenceKind;
  evidence_reference: string;
  reason: string;
  attachment: File | null;
  mfa_code: string;
}

/** A US legal entity of the group, by name. */
export interface UsEntity {
  legal_entity_id: string;
  name: string;
}

export interface RejectedInput {
  legal_entity_id: string;
  bank_transaction_id: string | null;
  legal_authority: string;
  description: string;
  rejected_on: string;
  mfa_code: string;
}

const BASE = '/api/v1/compliance/ofac';
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

class OfacBlockingService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async blocks(inForceOnly: boolean): Promise<BlockedProperty[]> {
    return (await this.api.get<BlockedProperty[]>(`${BASE}/blocks`, { params: { in_force_only: inForceOnly } })).data;
  }

  async tasks(openOnly: boolean): Promise<ReportTask[]> {
    return (await this.api.get<ReportTask[]>(`${BASE}/tasks`, { params: { open_only: openOnly } })).data;
  }

  async file(taskId: string, body: FilingInput): Promise<void> {
    const form = new FormData();
    form.append('filed_on', body.filed_on);
    form.append('ofac_reference', body.ofac_reference);
    form.append('mfa_code', body.mfa_code);
    form.append('attachment', body.attachment, body.attachment.name);
    await this.api.post(`${BASE}/tasks/${taskId}/filing`, form, MULTIPART);
  }

  /** The blocked cash was placed in the US entity's blocked interest-bearing account, or there is none. */
  async placement(taskId: string, body: PlacementInput): Promise<void> {
    const form = new FormData();
    form.append('mfa_code', body.mfa_code);
    if (body.placed_on) form.append('placed_on', body.placed_on);
    if (body.account_reference) form.append('account_reference', body.account_reference);
    if (body.bank_reference) form.append('bank_reference', body.bank_reference);
    if (body.amount) form.append('amount', body.amount);
    if (body.currency_code) form.append('currency_code', body.currency_code);
    if (body.no_cash_reason) form.append('no_cash_reason', body.no_cash_reason);
    if (body.attachment) form.append('attachment', body.attachment, body.attachment.name);
    await this.api.post(`${BASE}/tasks/${taskId}/placement`, form, MULTIPART);
  }

  async notRequired(taskId: string, reason: string, mfaCode: string): Promise<void> {
    await this.api.post(`${BASE}/tasks/${taskId}/not-required`, { reason, mfa_code: mfaCode });
  }

  /** A 15-minute link to the copy that was filed (opening it is audited). */
  async fileLink(taskId: string, placement = false): Promise<string> {
    return (await this.api.get<{ url: string }>(`${BASE}/tasks/${taskId}/file`, { params: { placement } })).data.url;
  }

  async recordEvidence(blockId: string, body: EvidenceInput): Promise<string> {
    const form = new FormData();
    form.append('evidence_kind', body.evidence_kind);
    form.append('evidence_reference', body.evidence_reference);
    form.append('reason', body.reason);
    form.append('mfa_code', body.mfa_code);
    if (body.attachment) form.append('attachment', body.attachment, body.attachment.name);
    const res = await this.api.post<{ request_id: string }>(
      `${BASE}/blocks/${blockId}/unblock-evidence`,
      form,
      MULTIPART,
    );
    return res.data.request_id;
  }

  /** A 15-minute link to the copy of the license or notice recorded for a release (opening it is audited). */
  async evidenceLink(requestId: string): Promise<string> {
    return (await this.api.get<{ url: string }>(`${BASE}/unblock-requests/${requestId}/file`)).data.url;
  }

  async confirmRelease(requestId: string, mfaCode: string): Promise<void> {
    await this.api.post(`${BASE}/unblock-requests/${requestId}/confirm`, { mfa_code: mfaCode });
  }

  async confirmedMatches(): Promise<ConfirmedMatch[]> {
    return (await this.api.get<ConfirmedMatch[]>(`${BASE}/confirmed-matches`)).data;
  }

  async recordDelisting(screeningResultId: number, body: DelistingInput): Promise<void> {
    const form = new FormData();
    form.append('evidence_reference', body.evidence_reference);
    form.append('reason', body.reason);
    form.append('mfa_code', body.mfa_code);
    if (body.attachment) form.append('attachment', body.attachment, body.attachment.name);
    await this.api.post(`${BASE}/confirmed-matches/${screeningResultId}/delisting-evidence`, form, MULTIPART);
  }

  /** A 15-minute link to the notice recorded for a delisting (opening it is audited). */
  async delistingLink(requestId: string): Promise<string> {
    return (await this.api.get<{ url: string }>(`${BASE}/delisting-requests/${requestId}/file`)).data.url;
  }

  async confirmDelisting(requestId: string, mfaCode: string): Promise<void> {
    await this.api.post(`${BASE}/delisting-requests/${requestId}/confirm`, { mfa_code: mfaCode });
  }

  /** The group's US legal entities (503 when funds cannot be asked: the list is never partial). */
  async usEntities(): Promise<UsEntity[]> {
    return (await this.api.get<UsEntity[]>(`${BASE}/us-entities`)).data;
  }

  async recordRejected(body: RejectedInput): Promise<void> {
    await this.api.post(`${BASE}/rejected-transactions`, body);
  }
}

export default new OfacBlockingService();
